import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { tenantAPI, subscriptionPlanAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import { MODULE_OPTIONS } from "../../lib/modules";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Building2,
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Ban,
  CheckCircle,
  Loader2,
} from "lucide-react";

const TenantsPage = () => {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: "",
    company_name: "",
    subscription_plan: "",
    max_employees: 50,
    enabled_modules: ["attendance", "leaves", "documents"],
  });

  useEffect(() => {
    fetchTenants();
    fetchPlans();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await tenantAPI.getAll();
      setTenants(response.data);
    } catch (error) {
      toast.error("Failed to fetch tenants");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await subscriptionPlanAPI.getAll();
      setPlans(response.data);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    }
  };

  const applyPlan = (planName) => {
    const plan = plans.find((p) => p.name === planName);
    if (plan) {
      setFormData((prev) => ({
        ...prev,
        subscription_plan: plan.name,
        enabled_modules: [...plan.modules],
        max_employees: plan.max_employees,
      }));
    } else {
      setFormData((prev) => ({ ...prev, subscription_plan: planName }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.subscription_plan) {
      toast.error("Please select a subscription plan");
      return;
    }
    setCreateLoading(true);
    try {
      await tenantAPI.create(formData);
      toast.success("Tenant created successfully");
      setShowCreateDialog(false);
      setFormData({
        tenant_id: "",
        company_name: "",
        subscription_plan: "",
        max_employees: 50,
        enabled_modules: ["attendance", "leaves", "documents"],
      });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tenant");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSuspend = async (tenantId) => {
    try {
      await tenantAPI.suspend(tenantId);
      toast.success("Tenant suspended");
      fetchTenants();
    } catch (error) {
      toast.error("Failed to suspend tenant");
    }
  };

  const handleActivate = async (tenantId) => {
    try {
      await tenantAPI.activate(tenantId);
      toast.success("Tenant activated");
      fetchTenants();
    } catch (error) {
      toast.error("Failed to activate tenant");
    }
  };

  const filteredTenants = tenants.filter(
    (tenant) =>
      (tenant.company_name || tenant.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tenant.tenant_id || tenant.domain || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tenants-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Tenants</h1>
          <p className="text-slate-500">Manage tenant organizations</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-tenant-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Tenant</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="form-group">
                <Label>Organization Name</Label>
                <Input
                  placeholder="Acme Corp"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  required
                  data-testid="tenant-name-input"
                />
              </div>
              <div className="form-group">
                <Label>Tenant ID</Label>
                <Input
                  placeholder="acme"
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                  required
                  data-testid="tenant-domain-input"
                />
              </div>
              <div className="form-group">
                <Label>Subscription Plan</Label>
                <Select
                  value={formData.subscription_plan}
                  onValueChange={applyPlan}
                >
                  <SelectTrigger data-testid="tenant-plan-select">
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.length === 0 ? (
                      <SelectItem value="none" disabled>No plans — create one first</SelectItem>
                    ) : (
                      plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.name}>
                          {plan.name} ({plan.modules.length} modules, up to {plan.max_employees})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Selecting a plan sets the enabled modules and max employees below.
                </p>
              </div>
              <div className="form-group">
                <Label>Max Employees</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.max_employees}
                  onChange={(e) => setFormData({ ...formData, max_employees: parseInt(e.target.value) })}
                  required
                  data-testid="tenant-max-employees-input"
                />
              </div>
              <div className="form-group">
                <Label>Enabled Modules</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {MODULE_OPTIONS.map((module) => (
                    <Badge
                      key={module.key}
                      variant={formData.enabled_modules.includes(module.key) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        const newModules = formData.enabled_modules.includes(module.key)
                          ? formData.enabled_modules.filter((m) => m !== module.key)
                          : [...formData.enabled_modules, module.key];
                        setFormData({ ...formData, enabled_modules: newModules });
                      }}
                    >
                      {module.label}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={createLoading} data-testid="submit-create-tenant">
                {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Tenant"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="tenant-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Employees</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No tenants found
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map((tenant) => (
                  <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-indigo-600" />
                        </div>
                        <span className="font-medium">{tenant.company_name || tenant.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{tenant.tenant_id || tenant.domain}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {tenant.subscription_plan}
                      </Badge>
                    </TableCell>
                    <TableCell>{tenant.max_employees}</TableCell>
                    <TableCell>
                      <Badge variant={tenant.status === "active" ? "default" : "destructive"} className="capitalize">
                        {tenant.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(tenant.created_at)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/tenants/${tenant.id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </Link>
                          </DropdownMenuItem>
                          {tenant.status === "active" ? (
                            <DropdownMenuItem
                              onClick={() => handleSuspend(tenant.id)}
                              className="text-red-600"
                            >
                              <Ban className="w-4 h-4 mr-2" />
                              Suspend
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleActivate(tenant.id)}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Activate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantsPage;
